import { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Focusable, enabled radios inside the group, in DOM order.
 *
 * The `[tabindex]` filter matters: the DS `SelectableListItem` renders its own
 * `role="radio"` indicator, which is `aria-hidden` and not focusable. Without
 * the filter, arrow navigation would try to focus those and go nowhere.
 */
const radiosIn = (container) =>
  container
    ? Array.from(
      container.querySelectorAll('[role="radio"][tabindex]:not([aria-disabled="true"])'),
    )
    : [];

/**
 * `role="radiogroup"` wrapper with arrow-key navigation (CT-5191).
 *
 * Mirrors what `TabNav` does for tabs: the DS has no radiogroup container, so
 * this is the one piece the pickers add rather than a component they replace.
 *
 * `columns` makes Up/Down move a whole row instead of one item, which is what
 * the entity picker's two-column grid needs (CT-5192).
 */
function PickerOptionGroup({ ariaLabel, columns = 1, className = '', children }) {
  const groupRef = useRef(null);

  const handleKeyDown = useCallback(
    (event) => {
      const radios = radiosIn(groupRef.current);
      if (!radios.length) return;

      const currentIndex = radios.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;
      switch (event.key) {
        case 'ArrowRight':
          nextIndex = (currentIndex + 1) % radios.length;
          break;
        case 'ArrowLeft':
          nextIndex = (currentIndex - 1 + radios.length) % radios.length;
          break;
        case 'ArrowDown':
          nextIndex = Math.min(radios.length - 1, currentIndex + columns);
          break;
        case 'ArrowUp':
          nextIndex = Math.max(0, currentIndex - columns);
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = radios.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      radios[nextIndex]?.focus();
    },
    [columns],
  );

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={className}
    >
      {children}
    </div>
  );
}

PickerOptionGroup.propTypes = {
  ariaLabel: PropTypes.string,
  columns: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default PickerOptionGroup;
